import React, { useState, useEffect } from "react";
import {
  Modal,
  Backdrop,
  Fade,
  makeStyles,
  Typography,
  ButtonGroup,
  Button,
  CircularProgress,
  Box,
} from "@material-ui/core";
import { Bar } from "react-chartjs-2";
import moment from "moment";
import api from "../../URL/axiosConfig";

// Registrasi ChartJS (Sama seperti sebelumnya)
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const useStyles = makeStyles(theme => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4),
    width: "70%",
    maxWidth: "1000px",
    borderRadius: "8px",
    maxHeight: "90vh",
    overflowY: "auto",
    outline: "none",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  chartContainer: {
    marginBottom: theme.spacing(4),
    height: "300px",
  },
}));

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    x: {
      grid: { display: false },
    },
    y: {
      beginAtZero: true,
      grid: { borderDash: [2, 2] },
    },
  },
};

const ModalViewStat = ({ open, onClose, dataId }) => {
  const classes = useStyles();
  const [filter, setFilter] = useState("30D");
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState({ send: null, receive: null });

  useEffect(() => {
    if (open && dataId) {
      fetchStatData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filter, dataId]);

  const fetchStatData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/action/gateway/traffic/${dataId}`, {
        params: { filter },
      });

      const data = Array.isArray(res.data.data) ? res.data.data : [];
      processData(data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const processData = rawData => {
    // 1. Tentukan format label berdasarkan filter
    // Jika data menit (HOUR), format Jam:Menit. Jika harian (30D), format Tanggal.
    let dateFormat = "Do"; // Default (1st, 2nd, etc.)
    if (filter === "HOUR") dateFormat = "HH:mm";
    if (filter === "1D") dateFormat = "HH:00";

    // 2. Buat Label Sumbu X (Shared)
    const labels = rawData.map(item =>
      moment(item.timestamp).format(dateFormat),
    );

    // 3. Setup Grafik 1: Send (Emitted & Received TX)
    const sendChart = {
      labels,
      datasets: [
        {
          label: "Emitted (Light)",
          // Ambil txPacketsEmitted dari array rawData
          data: rawData.map(d => d.txPacketsEmitted),
          backgroundColor: "#BAE7FF",
          barPercentage: 0.6,
          categoryPercentage: 0.8,
        },
        {
          label: "Received (Dark)",
          // Ambil txPacketsReceived dari array rawData
          data: rawData.map(d => d.txPacketsReceived),
          backgroundColor: "#1890FF",
          barPercentage: 0.6,
          categoryPercentage: 0.8,
        },
      ],
    };

    // 4. Setup Grafik 2: Receive (Received & Received OK)
    const receiveChart = {
      labels,
      datasets: [
        {
          label: "Received (Light)",
          // Ambil rxPacketsReceived dari array rawData
          data: rawData.map(d => d.rxPacketsReceived),
          backgroundColor: "#BAE7FF",
          barPercentage: 0.6,
          categoryPercentage: 0.8,
        },
        {
          label: "Received OK (Dark)",
          // Ambil rxPacketsReceivedOK dari array rawData
          data: rawData.map(d => d.rxPacketsReceivedOK),
          backgroundColor: "#1890FF",
          barPercentage: 0.6,
          categoryPercentage: 0.8,
        },
      ],
    };

    setChartData({ send: sendChart, receive: receiveChart });
  };

  return (
    <Modal
      className={classes.modal}
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          <div className={classes.header}>
            <Typography variant="h5">Statistic View</Typography>
            <ButtonGroup color="primary">
              {["HOUR", "1D", "14D", "30D"].map(type => (
                <Button
                  key={type}
                  variant={filter === type ? "contained" : "outlined"}
                  onClick={() => setFilter(type)}
                >
                  {type}
                </Button>
              ))}
            </ButtonGroup>
          </div>

          {loading ? (
            <Box display="flex" justifyContent="center" m={5}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Grafik Sent */}
              <Typography variant="h6" gutterBottom>
                Frames sent per day
              </Typography>
              <div className={classes.chartContainer}>
                {chartData.send ? (
                  <Bar data={chartData.send} options={chartOptions} />
                ) : (
                  <Typography align="center">No Data Available</Typography>
                )}
              </div>

              {/* Grafik Received */}
              <Typography variant="h6" gutterBottom>
                Frames received per day
              </Typography>
              <div className={classes.chartContainer}>
                {chartData.receive ? (
                  <Bar data={chartData.receive} options={chartOptions} />
                ) : (
                  <Typography align="center">No Data Available</Typography>
                )}
              </div>
            </>
          )}
        </div>
      </Fade>
    </Modal>
  );
};

export default ModalViewStat;
